import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";

import { useMsal } from "@azure/msal-react";

import { Check, ChevronRight, GitBranch, Maximize2, Minimize2, Pencil, Plus, X } from "lucide-react";



import { getToken, useMsalAuth } from "../../authConfig";

import { LoginContext } from "../../loginContext";

import {

    getCompanyNoteWorkflowsApi,

    NoteWorkflowsConflictError,

    updateCompanyNoteWorkflowsApi

} from "../../api/noteWorkflowsApi";

import { NoteWorkflowsCanvas } from "./NoteWorkflowsCanvas";

import { WorkflowNodeDialog } from "./WorkflowNodeDialog";
import { WorkflowBrowser } from "./WorkflowBrowser";
import type { WorkflowLibraryView } from "./workflowLibraryTypes";
import { selectedTaskSettings } from "./boxTaskTypes";
import type { BoxTaskSettings } from "./boxTaskTypes";
import { useOrganization } from "../../contexts/OrganizationContext";
import { getOrganizationMembers } from "../../api/organizationAccessApi";
import type { OrganizationMemberDirectory } from "../../pages/organization-admin/types";
import { AdminWorkflowChatWindow } from "../../features/organizationWorkflowBuilder/AdminWorkflowChatWindow";
import { AdminScheduledWorkflowLibrary } from "../../features/organizationWorkflowBuilder/AdminScheduledWorkflowLibrary";
import { ShiftHandoverLibrary } from "../../features/organizationWorkflowBuilder/ShiftHandoverLibrary";
import { deleteOrganizationWorkflow } from "../../features/organizationWorkflowBuilder/api";

import styles from "./NoteWorkflows.module.css";

import {

    EMPTY_NOTE_WORKFLOWS_DOCUMENT,

    NoteWorkflow,

    NoteWorkflowsDocument,

    WorkflowNodeSelection

} from "./noteWorkflowsTypes";

import {

    addOutcome,

    addStep,

    addSubflow,

    addSubflowStep,

    addWorkflow,

    cloneDocument,

    deleteNode,

    deleteSubflow,

    deleteSubflowStep,

    documentsEqual,

    updateNode,

    updateSubflowNode,

    updateWorkflowViewAllBoxes

} from "./noteWorkflowsMutations";

import { validateNoteWorkflowsDocument } from "./noteWorkflowsSchema";



function findNodeTitle(

    document: NoteWorkflowsDocument,

    selection: WorkflowNodeSelection

): string {

    const workflow = document.workflows.find(item => item.id === selection.workflowId);

    if (!workflow) {

        return "";

    }

    if (selection.nodeKind === "trigger") {

        return workflow.trigger.title ?? "";

    }

    if (selection.nodeKind === "step") {

        return workflow.steps.find(step => step.id === selection.nodeId)?.title ?? "";

    }

    if (selection.nodeKind === "subflow") {

        return workflow.subflows?.find(subflow => subflow.id === selection.nodeId)?.title ?? "";

    }

    if (selection.nodeKind === "subflow_step") {

        const subflow = workflow.subflows?.find(item => item.id === selection.subflowId);

        return subflow?.steps.find(step => step.id === selection.nodeId)?.title ?? "";

    }

    return workflow.outcomes.find(outcome => outcome.id === selection.nodeId)?.title ?? "";

}



function findNodeDescription(

    document: NoteWorkflowsDocument,

    selection: WorkflowNodeSelection

): string {

    const workflow = document.workflows.find(item => item.id === selection.workflowId);

    if (!workflow) {

        return "";

    }

    if (selection.nodeKind === "trigger") {

        return workflow.trigger.description;

    }

    if (selection.nodeKind === "step") {

        return workflow.steps.find(step => step.id === selection.nodeId)?.description ?? "";

    }

    if (selection.nodeKind === "subflow") {

        return workflow.subflows?.find(subflow => subflow.id === selection.nodeId)?.description ?? "";

    }

    if (selection.nodeKind === "subflow_step") {

        const subflow = workflow.subflows?.find(item => item.id === selection.subflowId);

        return subflow?.steps.find(step => step.id === selection.nodeId)?.description ?? "";

    }

    return workflow.outcomes.find(outcome => outcome.id === selection.nodeId)?.description ?? "";

}



function formatUpdatedAt(timestamp: number | undefined, locale: string): string {

    if (!timestamp) {

        return "";

    }

    return new Date(timestamp * 1000).toLocaleString(locale);

}



interface NoteWorkflowsEditorProps {
    variant?: "settings" | "embedded";
    onNodeDialogOpenChange?: (open: boolean) => void;
}

export function NoteWorkflowsEditor({ onNodeDialogOpenChange, variant = "settings" }: NoteWorkflowsEditorProps = {}) {

    const { t, i18n } = useTranslation();

    const { loggedIn } = useContext(LoginContext);

    const client = useMsalAuth ? useMsal().instance : undefined;

    const [saved, setSaved] = useState<NoteWorkflowsDocument | null>(null);

    const [draft, setDraft] = useState<NoteWorkflowsDocument>(cloneDocument(EMPTY_NOTE_WORKFLOWS_DOCUMENT));

    const [editing, setEditing] = useState(false);
    const [editingWorkflowId, setEditingWorkflowId] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [error, setError] = useState<string | null>(null);

    const [selected, setSelected] = useState<WorkflowNodeSelection | null>(null);

    const [dialogOpen, setDialogOpen] = useState(false);

    const [fullscreenOpen, setFullscreenOpen] = useState(false);
    const [editorWindowOpen, setEditorWindowOpen] = useState(false);
    const [chatSession, setChatSession] = useState<{ organizationId: string; workflow?: NoteWorkflow; scheduledDraftId?: string; handoverId?: string } | null>(null);
    const [scheduledLibraryVersion, setScheduledLibraryVersion] = useState(0);
    const { context, switching } = useOrganization();
    const organizationId = context?.currentOrganizationId;
    useEffect(() => { setChatSession(null); }, [organizationId]);
    const [directory, setDirectory] = useState<OrganizationMemberDirectory | null>(null);
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersError, setMembersError] = useState<string | null>(null);
    const [membersAttempt, setMembersAttempt] = useState(0);

    const isNodeDialogOpen = dialogOpen && editing && !!selected;

    useEffect(() => {
        onNodeDialogOpenChange?.(isNodeDialogOpen || editorWindowOpen);
    }, [isNodeDialogOpen, editorWindowOpen, onNodeDialogOpenChange]);

    useEffect(() => {
        return () => onNodeDialogOpenChange?.(false);
    }, [onNodeDialogOpenChange]);

    const workflowIsDirty = useMemo(() => {

        if (!saved) {

            return !documentsEqual(draft, EMPTY_NOTE_WORKFLOWS_DOCUMENT);

        }

        return !documentsEqual(draft, saved);

    }, [draft, saved]);
    const isDirty = workflowIsDirty;



    const getIdToken = useCallback(async () => {

        if (client && loggedIn) {

            try {

                return await getToken(client);

            } catch (err) {

                console.error("Failed to get auth token:", err);

                return undefined;

            }

        }

        return undefined;

    }, [client, loggedIn]);

    useEffect(() => {
        if (!editorWindowOpen && variant !== "embedded") return;
        let cancelled = false;
        setDirectory(null);
        setMembersError(null);
        setMembersLoading(false);
        if (!organizationId) {
            setMembersError(t("settingsPage.company.noteWorkflows.tasks.organizationRequired", { defaultValue: "Select an organization to assign members." }));
            return;
        }
        setMembersLoading(true);
        void getIdToken().then(token => getOrganizationMembers(organizationId, token)).then(result => {
            if (!cancelled) setDirectory(result);
        }).catch((error: unknown) => {
            if (!cancelled) setMembersError(error instanceof Error ? error.message : "Unable to load members");
        }).finally(() => { if (!cancelled) setMembersLoading(false); });
        return () => { cancelled = true; };
    }, [editorWindowOpen, variant, organizationId, getIdToken, membersAttempt, t]);



    const loadDocument = useCallback(async () => {

        try {

            setLoading(true);

            setError(null);

            const idToken = await getIdToken();

            const fetched = await getCompanyNoteWorkflowsApi(idToken);

            setSaved(fetched);

            setDraft(cloneDocument(fetched));

        } catch (err: unknown) {

            const message = err instanceof Error ? err.message : t("settingsPage.errors.loadFailed");

            setError(message);

        } finally {

            setLoading(false);

        }

    }, [getIdToken, t]);



    useEffect(() => {

        void loadDocument();

    }, [loadDocument]);



    useEffect(() => {

        if (!fullscreenOpen) {

            return;

        }

        const handleKeyDown = (event: KeyboardEvent) => {

            if (event.key === "Escape") {

                setFullscreenOpen(false);

            }

        };

        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);

    }, [fullscreenOpen]);



    const selectedWorkflow = draft.workflows.find(workflow =>
        workflow.id === (editingWorkflowId ?? selected?.workflowId)
    );

    const canAddStep = editing && !saving && !!selectedWorkflow;

    const canAddOutcome = editing && !saving && !!selectedWorkflow && (selectedWorkflow?.steps.length ?? 0) > 0;

    const canAddSubflow = editing && !saving && !!selectedWorkflow;

    const canAddSubflowStep =
        editing && !saving && selected?.nodeKind === "subflow" && !!selectedWorkflow;



    const startEditing = (workflowId: string) => {
        if (variant === "embedded") {
            const workflow = saved?.workflows.find(item => item.id === workflowId);
            if (workflow && organizationId) setChatSession({ organizationId, workflow });
            return;
        }
        setEditingWorkflowId(workflowId);
        setDraft(cloneDocument(saved ?? EMPTY_NOTE_WORKFLOWS_DOCUMENT));
        setSelected(null);
        setError(null);
        setEditing(true);
    };

    const startCreatingWorkflow = (): string => {
        const baseDocument = cloneDocument(saved ?? EMPTY_NOTE_WORKFLOWS_DOCUMENT);
        const { document: nextDocument, workflow } = addWorkflow(baseDocument);

        setEditingWorkflowId(workflow.id);
        setDraft(nextDocument);
        setSelected({
            workflowId: workflow.id,
            nodeKind: "trigger",
            nodeId: workflow.trigger.id
        });
        setError(null);
        setEditing(true);
        setDialogOpen(true);

        return workflow.id;
    };

    const changeEditorWindowOpen = (open: boolean) => {
        if (!open) {
            if (saving) return;
            if (isDirty && !window.confirm(t("settingsPage.company.noteWorkflows.discardChanges", {
                defaultValue: "Discard unsaved workflow changes?"
            }))) return;
            cancelEditing();
        }
        setEditorWindowOpen(open);
    };

    const cancelEditing = () => {

        setDraft(cloneDocument(saved ?? EMPTY_NOTE_WORKFLOWS_DOCUMENT));

        setSelected(null);

        setDialogOpen(false);

        setFullscreenOpen(false);

        setError(null);

        setEditing(false);
        setEditingWorkflowId(null);

    };



    const saveDocument = async () => {

        const validationError = validateNoteWorkflowsDocument(draft);

        if (validationError) {

            setError(validationError);

            return;

        }



        try {

            setSaving(true);

            setError(null);

            const idToken = await getIdToken();

            const updated = workflowIsDirty ? await updateCompanyNoteWorkflowsApi(draft, idToken) : draft;

            setSaved(updated);

            setDraft(cloneDocument(updated));

            setEditing(false);
        setEditingWorkflowId(null);

            setSelected(null);

            setDialogOpen(false);

            setFullscreenOpen(false);

        } catch (err: unknown) {

            if (err instanceof NoteWorkflowsConflictError) {

                setSaved(err.document);

                setDraft(prev => ({ ...prev, updated_at: err.document.updated_at, updated_by: err.document.updated_by }));

                setError(t("settingsPage.company.noteWorkflows.conflictError"));

                return;

            }

            const message = err instanceof Error ? err.message : t("settingsPage.errors.saveFailed");

            setError(message);

        } finally {

            setSaving(false);

        }

    };



    const handleAddWorkflow = () => {

        const { document: next, workflow } = addWorkflow(draft);

        setDraft(next);

        setSelected({

            workflowId: workflow.id,

            nodeKind: "trigger",

            nodeId: workflow.trigger.id

        });

    };



    const handleAddStep = () => {

        if (!selectedWorkflow) {

            return;

        }

        const { document: next, step } = addStep(draft, selectedWorkflow.id);

        setDraft(next);

        setSelected({

            workflowId: selectedWorkflow.id,

            nodeKind: "step",

            nodeId: step.id

        });

        setDialogOpen(true);

    };



    const handleAddOutcome = () => {

        if (!selectedWorkflow) {

            return;

        }

        const { document: next, outcome } = addOutcome(draft, selectedWorkflow.id);

        setDraft(next);

        setSelected({

            workflowId: selectedWorkflow.id,

            nodeKind: "outcome",

            nodeId: outcome.id

        });

        setDialogOpen(true);

    };



    const handleAddSubflow = () => {

        if (!selectedWorkflow) {

            return;

        }

        const { document: next, subflow, step } = addSubflow(draft, selectedWorkflow.id);

        setDraft(next);

        setSelected({

            workflowId: selectedWorkflow.id,

            nodeKind: "subflow_step",

            subflowId: subflow.id,

            nodeId: step.id

        });

        setDialogOpen(true);

    };



    const handleAddSubflowStep = () => {

        if (!selectedWorkflow || selected?.nodeKind !== "subflow") {

            return;

        }

        const { document: next, step } = addSubflowStep(draft, selectedWorkflow.id, selected.nodeId);

        setDraft(next);

        setSelected({

            workflowId: selectedWorkflow.id,

            nodeKind: "subflow_step",

            subflowId: selected.nodeId,

            nodeId: step.id

        });

        setDialogOpen(true);

    };



    const handleNodeSelect = (selection: WorkflowNodeSelection) => {

        setSelected(selection);

        if (editing) {

            setDialogOpen(true);

        }

    };



    const handleSaveNode = (title: string, description: string, taskSettings?: BoxTaskSettings) => {

        if (!selected) {

            return;

        }

        if (selected.nodeKind === "subflow" || selected.nodeKind === "subflow_step") {

            setDraft(

                updateSubflowNode(

                    draft,

                    selected.workflowId,

                    selected.nodeKind,

                    selected.nodeId,

                    { title, description, task_settings: taskSettings },

                    selected.nodeKind === "subflow_step" ? selected.subflowId : undefined

                )

            );

            return;

        }

        setDraft(

            updateNode(draft, selected.workflowId, selected.nodeKind, selected.nodeId, {

                title,

                description,
                task_settings: taskSettings

            })

        );

    };



    const handleDeleteNode = () => {

        if (!selected) {

            return;

        }

        if (selected.nodeKind === "subflow") {

            setDraft(deleteSubflow(draft, selected.workflowId, selected.nodeId));

            setSelected(null);

            return;

        }

        if (selected.nodeKind === "subflow_step") {

            setDraft(

                deleteSubflowStep(draft, selected.workflowId, selected.subflowId, selected.nodeId)

            );

            setSelected(null);

            return;

        }

        const next = deleteNode(draft, selected.workflowId, selected.nodeKind, selected.nodeId);

        setDraft(next);

        setSelected(null);

    };



    const activeDocument = editing ? draft : saved ?? EMPTY_NOTE_WORKFLOWS_DOCUMENT;

    const workflowCount = activeDocument.workflows.length;

    const updatedAt = formatUpdatedAt((saved ?? draft).updated_at, i18n.language);

    const updatedBy = (saved ?? draft).updated_by;



    const toolbar = editing ? (

        <div className={styles.toolbar}>

            {variant === "settings" && !editorWindowOpen && <button type="button" className={styles.toolbarButton} onClick={handleAddWorkflow}>

                <Plus size={14} />

                {t("settingsPage.company.noteWorkflows.actions.newWorkflow")}

            </button>}

            <button

                type="button"

                className={styles.toolbarButton}

                onClick={handleAddStep}

                disabled={!canAddStep}

            >

                <Plus size={14} />

                {t("settingsPage.company.noteWorkflows.actions.addStep")}

            </button>

            <button

                type="button"

                className={styles.toolbarButton}

                onClick={handleAddOutcome}

                disabled={!canAddOutcome}

            >

                <Plus size={14} />

                {t("settingsPage.company.noteWorkflows.actions.addOutcome")}

            </button>

            <button

                type="button"

                className={styles.toolbarButton}

                onClick={handleAddSubflow}

                disabled={!canAddSubflow}

            >

                <Plus size={14} />

                {t("settingsPage.company.noteWorkflows.actions.addSubflow")}

            </button>

            <button

                type="button"

                className={styles.toolbarButton}

                onClick={handleAddSubflowStep}

                disabled={!canAddSubflowStep}

            >

                <Plus size={14} />

                {t("settingsPage.company.noteWorkflows.actions.addSubflowStep")}

            </button>

            <label
                className={styles.workflowViewAllToggle}
                title={t("settingsPage.company.noteWorkflows.viewAllBoxesHelp", {
                    defaultValue: "After submission, active members of the same organization with existing project access can see all boxes in new cards from this workflow, regardless of per-box view lists. Saved box permissions and signing requirements are unchanged."
                })}
            >
                <input
                    type="checkbox"
                    checked={selectedWorkflow?.view_all_boxes === true}
                    disabled={saving || !selectedWorkflow}
                    onChange={event => {
                        if (!selectedWorkflow) return;
                        const enabled = event.currentTarget.checked;
                        setDraft(current => updateWorkflowViewAllBoxes(current, selectedWorkflow.id, enabled));
                    }}
                />
                <span>{t("settingsPage.company.noteWorkflows.viewAllBoxes", { defaultValue: "Everyone can view all boxes" })}</span>
            </label>

        </div>

    ) : null;



    const canvasProps = {

        document: activeDocument,

        selected,

        onNodeSelect: handleNodeSelect

    };



    const deleteWorkflow = async (workflow: NoteWorkflow, signal: AbortSignal): Promise<void> => {
        if (!organizationId || switching || editing || saving) {
            throw new Error(t("settingsPage.company.noteWorkflows.deleteError"));
        }
        const token = await getIdToken();
        signal.throwIfAborted();
        const updated = await deleteOrganizationWorkflow(organizationId, workflow, token, signal);
        if (signal.aborted) return;
        setSaved(updated);
        setDraft(cloneDocument(updated));
        setError(null);
    };

    const reloadWorkflows = async (signal: AbortSignal): Promise<void> => {
        const token = await getIdToken();
        signal.throwIfAborted();
        const updated = await getCompanyNoteWorkflowsApi(token);
        if (signal.aborted) return;
        setSaved(updated);
        setDraft(cloneDocument(updated));
        setError(null);
    };

    const renderWorkflowBrowser = (libraries: readonly WorkflowLibraryView[] = []) => (
        <WorkflowBrowser
            key={`${organizationId ?? "none"}:${switching}`}
            title={variant === "embedded" ? t("organizationWorkflowBuilder.documentWorkflows", { defaultValue: "Organization workflows" }) : undefined}
            libraries={libraries}
            document={activeDocument}
            loading={loading}
            error={error}
            editing={editing}
            saving={saving}
            dirty={isDirty}
            toolbar={toolbar}
            selectedNode={selected}
            onEdit={startEditing}
            onCreate={() => {
                if (variant === "embedded") {
                    if (organizationId) setChatSession({ organizationId });
                    return null;
                }
                return startCreatingWorkflow();
            }}
            onCancel={cancelEditing}
            onSave={() => void saveDocument()}
            onDelete={deleteWorkflow}
            onReload={reloadWorkflows}
            deletionDisabled={!organizationId || switching}
            onNodeSelect={handleNodeSelect}
        />
    );

    const nodeDialog = isNodeDialogOpen ? (
        <WorkflowNodeDialog
            open={isNodeDialogOpen}
            selection={selected}
            title={selected ? findNodeTitle(draft, selected) : ""}
            description={selected ? findNodeDescription(draft, selected) : ""}
            onOpenChange={setDialogOpen}
            taskSettings={selectedTaskSettings(draft, selected)}
            directory={directory}
            membersLoading={membersLoading}
            membersError={membersError}
            onRetryMembers={() => setMembersAttempt(value => value + 1)}
            onSave={handleSaveNode}
            onDelete={handleDeleteNode}
        />
    ) : null;

    if (variant === "embedded") {
        return (
            <div className={styles.embeddedBrowser}>
                {organizationId ? <AdminScheduledWorkflowLibrary
                    key={`${organizationId}:${switching}:${scheduledLibraryVersion}`} organizationId={organizationId} switching={switching}
                    onCreate={() => setChatSession({ organizationId })}
                    onEdit={scheduledDraftId => setChatSession({ organizationId, scheduledDraftId })}
                    renderLibrary={scheduled => <ShiftHandoverLibrary organizationId={organizationId}
                        onCreate={() => setChatSession({ organizationId })}
                        onEdit={handoverId => setChatSession({ organizationId, handoverId })}
                        renderLibrary={handover => renderWorkflowBrowser([scheduled, handover])} />}
                /> : renderWorkflowBrowser()}
                {nodeDialog}
                {chatSession && chatSession.organizationId === organizationId && <AdminWorkflowChatWindow initialWorkflow={chatSession.workflow}
                    initialHandoverId={chatSession.handoverId} onHandoverSaved={() => {
                        setChatSession(null); setScheduledLibraryVersion(value => value + 1);
                    }}
                    initialScheduledDraftId={chatSession.scheduledDraftId} onClose={() => setChatSession(null)} onScheduledClose={() => {
                        setChatSession(null); setScheduledLibraryVersion(value => value + 1);
                    }} onSaved={updated => {
                    setSaved(updated);
                    setDraft(cloneDocument(updated));
                    setEditing(false); setEditingWorkflowId(null); setSelected(null); setError(null);
                    setChatSession(null);
                }} />}
            </div>
        );
    }

    return (

        <Dialog.Root open={editorWindowOpen} onOpenChange={changeEditorWindowOpen}>
        <div className={styles.section}>

            <details className={styles.disclosure} open>

                <summary className={styles.summary}>

                    <ChevronRight size={16} />

                    <GitBranch size={18} />

                    <span className={styles.summaryTitle}>

                        {t("settingsPage.company.noteWorkflows.title")}

                    </span>

                    <span className={styles.summaryMeta}>

                        {t("settingsPage.company.noteWorkflows.workflowCount", { count: workflowCount })}

                    </span>

                </summary>



                <div className={styles.body}>

                    <p className={styles.description}>{t("settingsPage.company.noteWorkflows.description")}</p>



                    <div className={styles.actionsRow}>

                        {!editing || editorWindowOpen ? (

                            <Dialog.Trigger asChild>
                            <button type="button" className={styles.editButton}>

                                <Pencil size={16} />

                                {t("settingsPage.account.edit")}

                            </button>
                            </Dialog.Trigger>

                        ) : (

                            <>

                                <button

                                    type="button"

                                    className={styles.cancelButton}

                                    onClick={cancelEditing}

                                    disabled={saving}

                                >

                                    <X size={16} />

                                    {t("settingsPage.account.cancel")}

                                </button>

                                <button

                                    type="button"

                                    className={styles.saveButton}

                                    onClick={() => void saveDocument()}

                                    disabled={saving || !isDirty}

                                >

                                    <Check size={16} />

                                    {saving

                                        ? t("settingsPage.account.saving")

                                        : t("settingsPage.account.save")}

                                </button>

                            </>

                        )}

                    </div>



                    {!editorWindowOpen && toolbar}



                    {error ? <p className={styles.errorText}>{error}</p> : null}

                    {loading ? <p className={styles.emptyState}>{t("settingsPage.loading")}</p> : null}



                    {!loading && workflowCount === 0 ? (

                        <p className={styles.emptyState}>{t("settingsPage.company.noteWorkflows.empty")}</p>

                    ) : null}



                    {!loading && workflowCount > 0 ? (

                        <div className={styles.canvasWrapper}>

                            <button

                                type="button"

                                className={styles.canvasExpandButton}

                                onClick={() => setFullscreenOpen(true)}

                                aria-label={t("settingsPage.company.noteWorkflows.expandCanvas")}

                            >

                                <Maximize2 size={16} />

                            </button>

                            <NoteWorkflowsCanvas {...canvasProps} variant="inline" />

                        </div>

                    ) : null}



                    {updatedAt ? (

                        <p className={styles.metaText}>

                            {t("settingsPage.company.noteWorkflows.lastUpdated", {

                                who: updatedBy || t("settingsPage.company.unknownEditor"),

                                when: updatedAt

                            })}

                        </p>

                    ) : (

                        <p className={styles.metaText}>{t("settingsPage.company.noteWorkflows.neverUpdated")}</p>

                    )}

                </div>

            </details>



            {fullscreenOpen ? (

                <div className={styles.fullscreenOverlay} role="dialog" aria-modal="true">

                    <div className={styles.fullscreenPanel}>

                        <div className={styles.fullscreenHeader}>

                            <span className={styles.fullscreenTitle}>

                                {t("settingsPage.company.noteWorkflows.title")}

                            </span>

                            <button

                                type="button"

                                className={styles.fullscreenCloseButton}

                                onClick={() => setFullscreenOpen(false)}

                                aria-label={t("settingsPage.company.noteWorkflows.collapseCanvas")}

                            >

                                <Minimize2 size={18} />

                            </button>

                        </div>

                        {!editorWindowOpen && toolbar}

                        <div className={styles.fullscreenBody}>

                            <NoteWorkflowsCanvas {...canvasProps} variant="fullscreen" />

                        </div>

                    </div>

                </div>

            ) : null}





        </div>
        <Dialog.Portal>
            <Dialog.Overlay className={styles.editorWindowOverlay} />
            <Dialog.Content
                className={styles.editorWindow}
                aria-describedby={undefined}
                onEscapeKeyDown={event => {
                    event.stopPropagation();
                    if (isNodeDialogOpen) {
                        event.preventDefault();
                        setDialogOpen(false);
                    }
                }}
            >
                <Dialog.Title className={styles.editorWindowAccessibleTitle}>
                    {t("settingsPage.company.noteWorkflows.title")}
                </Dialog.Title>
                <Dialog.Close asChild>
                    <button
                        type="button"
                        className={`${styles.fullscreenCloseButton} ${styles.editorWindowClose}`}
                        aria-label={t("settingsPage.close")}
                    >
                        <X size={20} />
                    </button>
                </Dialog.Close>
                <div className={styles.editorWindowBody}>
                    {renderWorkflowBrowser()}
                </div>
            {nodeDialog}
            </Dialog.Content>
        </Dialog.Portal>
        </Dialog.Root>

    );

}
